import { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Lock, Unlock } from 'lucide-react';
import { api } from '@/lib/api.js';
import { qk } from '@/lib/queryKeys.js';
import { cn } from '@/lib/cn.js';
import { formatDate } from '@/lib/format.js';
import { usePeriodStore } from '@/stores/period.js';
import { Button } from '@/components/ui/Button.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { DatePicker } from '@/components/DatePicker.jsx';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ─── shift color palette ──────────────────────────────────────────────────────
const SHIFT_COLORS = [
  'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
  'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
];

function shiftColor(shiftId) {
  if (!shiftId) return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400';
  return SHIFT_COLORS[(shiftId - 1) % SHIFT_COLORS.length];
}

const OVERRIDE_RING = 'ring-2 ring-inset ring-brand-500';

// ─── Day type styles ─────────────────────────────────────────────────────────
const DAY_TYPE_STYLES = {
  holiday:    'bg-rose-50   text-rose-400   dark:bg-rose-950/30  dark:text-rose-500',
  weekly_off: 'bg-slate-100 text-slate-400  dark:bg-slate-800    dark:text-slate-500',
  half_day:   'bg-amber-50  text-amber-600  dark:bg-amber-950/30 dark:text-amber-400',
};

function parseIsoDateLocal(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatIsoDateLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeDateKey(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  return formatIsoDateLocal(new Date(value));
}

function getDayType(emp, dateStr, dow, shiftById, holidayMap) {
  if (holidayMap[dateStr]) return 'holiday';
  const effectiveShiftId = emp.overrides[dateStr]?.shift_id ?? emp.shift_id;
  const shift = shiftById[effectiveShiftId];
  if (dow === 6 && shift?.saturday_type === 'holiday')  return 'weekly_off';
  if (dow === 6 && shift?.saturday_type === 'half_day') return 'half_day';
  if (dow === 0 && shift?.sunday_type   === 'holiday')  return 'weekly_off';
  if (dow === 0 && shift?.sunday_type   === 'half_day') return 'half_day';
  return 'working';
}

// ─── Cell popover ─────────────────────────────────────────────────────────────
function CellPopover({ employee, date, override, shifts, onAssign, onRemove, onClose }) {
  const [selectedShift, setSelectedShift] = useState(override?.shift_id ?? employee.shift_id ?? '');

  return (
    <div className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-1 w-52 rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-700 shadow-xl p-3 space-y-3">
      <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{employee.full_name}</p>
      <p className="text-xs text-slate-500">{formatDate(date)}</p>
      <Select
        value={selectedShift}
        onChange={(e) => setSelectedShift(Number(e.target.value) || '')}
        className="text-xs"
      >
        <option value="">— none —</option>
        {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </Select>
      <div className="flex gap-2">
        <Button
          size="xs"
          className="flex-1 text-xs"
          onClick={() => { onAssign(selectedShift); onClose(); }}
          disabled={!selectedShift}
        >
          Apply
        </Button>
        {override && (
          <Button
            size="xs"
            variant="outline"
            className="text-xs text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400"
            onClick={() => { onRemove(); onClose(); }}
          >
            Remove
          </Button>
        )}
        <Button size="xs" variant="ghost" className="text-xs" onClick={onClose}>✕</Button>
      </div>
    </div>
  );
}

function monthRange(y, m) {
  const pad = (n) => String(n).padStart(2, '0');
  const first = `${y}-${pad(m)}-01`;
  const last = new Date(y, m, 0).getDate();
  return [first, `${y}-${pad(m)}-${pad(last)}`];
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ShiftCalendarPage() {
  const { year, month, setPeriod } = usePeriodStore();
  const [deptFilter, setDeptFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('');
  const [activeCell, setActiveCell] = useState(null); // { empId, date }
  const [weekStart, setWeekStart] = useState(1);
  // Bulk assign state
  const [bulkShiftId, setBulkShiftId] = useState('');
  const [bulkDateFrom, setBulkDateFrom] = useState(() => monthRange(year, month)[0]);
  const [bulkDateTo, setBulkDateTo] = useState(() => monthRange(year, month)[1]);
  const [bulkSelectedEmps, setBulkSelectedEmps] = useState([]); // [] = all visible
  const [bulkSkipNonWorking, setBulkSkipNonWorking] = useState(true);

  const { toast } = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    setActiveCell(null);
    setWeekStart(1);
    const [from, to] = monthRange(year, month);
    setBulkDateFrom(from);
    setBulkDateTo(to);
  }, [year, month]);

  // ── Data fetches ────────────────────────────────────────────────────────────
  const calendarParams = new URLSearchParams({ year, month });
  if (deptFilter)           calendarParams.set('dept_id', deptFilter);
  if (categoryFilter)       calendarParams.set('employee_category_id', categoryFilter);
  if (employmentTypeFilter) calendarParams.set('employment_type', employmentTypeFilter);

  const { data: calData, isLoading } = useQuery({
    queryKey: [...qk.shiftCalendar(year, month), deptFilter, categoryFilter, employmentTypeFilter],
    queryFn: () => api.get(`/shift-calendar/month?${calendarParams}`),
    staleTime: 0,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get('/departments'),
  });

  const { data: shifts = [] } = useQuery({
    queryKey: qk.shifts,
    queryFn: () => api.get('/shifts'),
  });

  const { data: empCategories = [] } = useQuery({
    queryKey: qk.employeeCategories,
    queryFn: () => api.get('/employee-categories'),
  });

  const employees = useMemo(
    () => (calData?.employees ?? []).map((emp) => ({
      ...emp,
      overrides: Object.fromEntries(
        Object.entries(emp.overrides ?? {}).map(([dateKey, value]) => [normalizeDateKey(dateKey), value]),
      ),
    })),
    [calData?.employees],
  );
  const daysInMonth = calData?.days_in_month ?? 31;
  const isLocked = calData?.locked ?? false;
  const lockReason = calData?.reason ?? null;
  const rawHolidays = calData?.holidays ?? [];

  const holidayMap = useMemo(
    () => Object.fromEntries(rawHolidays.map((h) => [normalizeDateKey(h.date), h.name])),
    [rawHolidays],
  );

  const shiftById = useMemo(
    () => Object.fromEntries(shifts.map((s) => [s.id, s])),
    [shifts],
  );

  // Build array of day objects for the month
  const days = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = i + 1;
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dow = new Date(year, month - 1, d).getDay(); // 0=Sun 6=Sat
      return { d, dateStr, dow, isWeekend: dow === 0 || dow === 6 };
    });
  }, [year, month, daysInMonth]);

  // Slice to current week window (7 days max)
  const visibleDays = useMemo(
    () => days.slice(weekStart - 1, weekStart + 6),
    [days, weekStart],
  );
  const weekEnd = Math.min(weekStart + 6, daysInMonth);
  const weekLabel = `${MONTH_NAMES[month - 1]} ${weekStart} – ${weekEnd}, ${year}`;

  // ── Mutations ───────────────────────────────────────────────────────────────
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.shiftCalendar(year, month) });

  const bulkAssign = useMutation({
    mutationFn: (body) => api.post('/shift-calendar/bulk-assign', body),
    onSuccess: () => { invalidate(); toast({ variant: 'success', title: 'Shifts assigned' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const removeEntry = useMutation({
    mutationFn: (body) => api.raw('DELETE', '/shift-calendar/entry', body),
    onSuccess: () => { invalidate(); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const lockMutation = useMutation({
    mutationFn: () => api.post(`/shift-calendar/lock/${year}/${month}`),
    onSuccess: () => { invalidate(); toast({ variant: 'success', title: 'Month locked' }); },
    onError: (e) => toast({ variant: 'error', title: 'Lock failed', description: e.message }),
  });

  const unlockMutation = useMutation({
    mutationFn: () => api.del(`/shift-calendar/lock/${year}/${month}`),
    onSuccess: () => { invalidate(); toast({ variant: 'success', title: 'Month unlocked' }); },
    onError: (e) => toast({ variant: 'error', title: 'Unlock failed', description: e.message }),
  });

  // ── Navigation ──────────────────────────────────────────────────────────────
  function prevMonth() {
    const newYear = month === 1 ? year - 1 : year;
    const newMonth = month === 1 ? 12 : month - 1;
    setPeriod(newYear, newMonth);
  }
  function nextMonth() {
    const newYear = month === 12 ? year + 1 : year;
    const newMonth = month === 12 ? 1 : month + 1;
    setPeriod(newYear, newMonth);
  }
  function prevWeek() { setWeekStart((w) => Math.max(1, w - 7)); setActiveCell(null); }
  function nextWeek() { setWeekStart((w) => (w + 7 <= daysInMonth ? w + 7 : w)); setActiveCell(null); }

  // ── Bulk assign handler ──────────────────────────────────────────────────────
  function applyBulk() {
    if (!bulkShiftId || !bulkDateFrom || !bulkDateTo) {
      toast({ variant: 'error', title: 'Fill in all bulk assign fields' });
      return;
    }
    const from = parseIsoDateLocal(bulkDateFrom);
    const to   = parseIsoDateLocal(bulkDateTo);
    if (from > to) { toast({ variant: 'error', title: 'Date From must be ≤ Date To' }); return; }

    const allDates = [];
    for (let cur = new Date(from); cur <= to; cur.setDate(cur.getDate() + 1)) {
      allDates.push(formatIsoDateLocal(cur));
    }

    const empIds = bulkSelectedEmps.length > 0
      ? bulkSelectedEmps
      : employees.map((e) => e.id);

    // When skipping non-working days we filter per-employee using their effective shift
    if (bulkSkipNonWorking) {
      // Group employees by effective shift so we can batch per shift
      const shiftGroups = {};
      for (const id of empIds) {
        const emp = employees.find((e) => e.id === id);
        const sid = emp?.shift_id ?? 0;
        if (!shiftGroups[sid]) shiftGroups[sid] = [];
        shiftGroups[sid].push(id);
      }
      // Build a dummy emp-like object per group to check day types
      for (const [sid, ids] of Object.entries(shiftGroups)) {
        const dummyEmp = { shift_id: Number(sid), overrides: {} };
        const filteredDates = allDates.filter((ds) => {
          const dow = parseIsoDateLocal(ds).getDay();
          return getDayType(dummyEmp, ds, dow, shiftById, holidayMap) === 'working';
        });
        if (filteredDates.length > 0) {
          bulkAssign.mutate({ employee_ids: ids, shift_id: Number(bulkShiftId), dates: filteredDates });
        }
      }
      return;
    }

    bulkAssign.mutate({ employee_ids: empIds, shift_id: Number(bulkShiftId), dates: allDates });
  }

  // ── Cell click ──────────────────────────────────────────────────────────────
  function handleCellClick(empId, dateStr) {
    if (isLocked) return;
    setActiveCell((prev) => (prev?.empId === empId && prev?.date === dateStr ? null : { empId, date: dateStr }));
  }

  const isoMonth = `${year}-${String(month).padStart(2, '0')}`;

  return (
    <div className="space-y-4 " onClick={(e) => { if (activeCell && !e.target.closest('[data-popover]')) setActiveCell(null); }}>

      {/* ── Lock Banner ─────────────────────────────────────────────────────── */}
      {isLocked && (
        <div className={cn(
          'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium',
          lockReason === 'payroll'
            ? 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-950/30 dark:border-red-800 dark:text-red-300'
            : 'bg-amber-50 border border-amber-200 text-amber-700 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300',
        )}>
          <Lock className="h-4 w-4" />
          {lockReason === 'payroll'
            ? `${MONTH_NAMES[month - 1]} ${year} is locked — payroll has been finalized`
            : `${MONTH_NAMES[month - 1]} ${year} is manually locked`}
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        {/* Row 1: nav + lock button */}
        <div className="flex items-center gap-3">
          {/* Month nav */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="min-w-[140px] text-center font-semibold text-slate-800 dark:text-slate-100">
              {MONTH_NAMES[month - 1]} {year}
            </span>
            <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="h-4 w-4" /></Button>
          </div>

          {/* Week nav */}
          <div className="flex items-center gap-1 border-l border-slate-200 dark:border-slate-700 pl-3">
            <Button variant="ghost" size="sm" onClick={prevWeek} disabled={weekStart === 1}>
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Prev
            </Button>
            <span className="min-w-[160px] text-center text-sm text-slate-600 dark:text-slate-400">
              {weekLabel}
            </span>
            <Button variant="ghost" size="sm" onClick={nextWeek} disabled={weekStart + 7 > daysInMonth}>
              Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>

          {/* Lock / Unlock — top-right */}
          <div className="ml-auto flex gap-2">
            {isLocked && lockReason === 'manual' && (
              <Button variant="outline" size="sm" onClick={() => unlockMutation.mutate()} disabled={unlockMutation.isPending}>
                <Unlock className="h-3.5 w-3.5 mr-1.5" /> Unlock
              </Button>
            )}
            {!isLocked && (
              <Button variant="outline" size="sm" onClick={() => lockMutation.mutate()} disabled={lockMutation.isPending}>
                <Lock className="h-3.5 w-3.5 mr-1.5" /> Lock Month
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: Filters */}
        <div className="flex items-center gap-3">
          <Select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} className="text-sm w-40">
            <option value="">All Departments</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>

          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="text-sm w-40">
            <option value="">All Categories</option>
            {empCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>

          <Select value={employmentTypeFilter} onChange={(e) => setEmploymentTypeFilter(e.target.value)} className="text-sm w-44">
            <option value="">All Emp. Types</option>
            <option value="permanent">Permanent</option>
            <option value="contract">Contract</option>
            <option value="probation">Probation</option>
            <option value="intern">Intern</option>
          </Select>
        </div>
      </div>

      {/* ── Bulk Assign Panel ────────────────────────────────────────────────── */}
      {!isLocked && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500 font-medium">Shift</span>
            <Select value={bulkShiftId} onChange={(e) => setBulkShiftId(e.target.value)} className="text-sm w-44">
              <option value="">— select shift —</option>
              {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500 font-medium">From</span>
            <DatePicker
              value={bulkDateFrom}
              min={`${isoMonth}-01`}
              max={`${isoMonth}-${String(daysInMonth).padStart(2, '0')}`}
              onChange={(e) => setBulkDateFrom(e.target.value)}
              className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500 font-medium">To</span>
            <DatePicker
              value={bulkDateTo}
              min={`${isoMonth}-01`}
              max={`${isoMonth}-${String(daysInMonth).padStart(2, '0')}`}
              onChange={(e) => setBulkDateTo(e.target.value)}
              className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500 font-medium">Employees ({bulkSelectedEmps.length || 'all visible'})</span>
            <div className="flex gap-1">
              <Button size="md" onClick={applyBulk} disabled={bulkAssign.isPending}>
                Apply
              </Button>
              {bulkSelectedEmps.length > 0 && (
                <Button size="md" variant="outline" onClick={() => setBulkSelectedEmps([])}>Clear</Button>
              )}
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 cursor-pointer self-end pb-1">
            <input
              type="checkbox"
              checked={bulkSkipNonWorking}
              onChange={(e) => setBulkSkipNonWorking(e.target.checked)}
              className="h-3 w-3 rounded border-slate-300 accent-brand-500"
            />
            Skip holidays &amp; off-days
          </label>
        </div>
      )}

      {/* ── Roster Grid ─────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : employees.length === 0 ? (
        <div className="text-center py-12 text-slate-400">No active employees match the filters</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 min-h-[480px]">
          <table className="min-w-max text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/60">
                {/* Sticky employee column header */}
                <th className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-800/60 border-b border-r border-slate-200 dark:border-slate-700 px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300 w-56 min-w-[224px]">
                  Employee
                </th>
                {visibleDays.map(({ d, dateStr, dow, isWeekend }) => {
                  const isHoliday = !!holidayMap[dateStr];
                  return (
                    <th
                      key={d}
                      className={cn(
                        'border-b border-r border-slate-200 dark:border-slate-700 px-2 py-2 font-medium text-slate-500 dark:text-slate-400 w-20 min-w-[80px] text-center',
                        isWeekend && 'bg-slate-100/80 dark:bg-slate-800/30',
                        isHoliday && 'bg-rose-50/80 dark:bg-rose-950/20',
                      )}
                    >
                      <div className="text-[11px] font-semibold">{d}</div>
                      <div className="text-[10px] font-normal text-slate-400 dark:text-slate-500">
                        {parseIsoDateLocal(dateStr).toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      {isHoliday && (
                        <div className="text-[9px] font-medium text-rose-500 dark:text-rose-400 truncate leading-tight" title={holidayMap[dateStr]}>
                          {holidayMap[dateStr]}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.id} className="group hover:bg-brand-50/30 dark:hover:bg-brand-900/10">
                  {/* Sticky employee info */}
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-brand-50/40 dark:group-hover:bg-brand-900/10 border-b border-r border-slate-200 dark:border-slate-700 px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      {/* Bulk select checkbox */}
                      {!isLocked && (
                        <input
                          type="checkbox"
                          checked={bulkSelectedEmps.includes(emp.id)}
                          onChange={(e) => {
                            setBulkSelectedEmps((prev) =>
                              e.target.checked ? [...prev, emp.id] : prev.filter((id) => id !== emp.id),
                            );
                          }}
                          className="h-3 w-3 rounded border-slate-300 accent-brand-500"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 dark:text-slate-100 truncate max-w-[160px]">{emp.full_name}</div>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          {emp.department_name && (
                            <span className="rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-1.5 py-0 text-[10px]">
                              {emp.department_name}
                            </span>
                          )}
                          {emp.default_shift_name && (
                            <span className={cn('rounded-full px-1.5 py-0 text-[10px]', shiftColor(emp.shift_id))}>
                              {emp.default_shift_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Date cells */}
                  {visibleDays.map(({ d, dateStr, dow, isWeekend }) => {
                    const override = emp.overrides[dateStr];
                    const isOpen = activeCell?.empId === emp.id && activeCell?.date === dateStr;
                    const dayType = getDayType(emp, dateStr, dow, shiftById, holidayMap);
                    const isNonWorking = dayType !== 'working';
                    return (
                      <td
                        key={d}
                        className={cn(
                          'relative border-b border-r border-slate-200 dark:border-slate-700 p-0.5 text-center align-middle',
                          isWeekend && 'bg-slate-50/60 dark:bg-slate-800/20',
                          dayType === 'holiday'    && 'bg-rose-50/50 dark:bg-rose-950/10',
                        )}
                      >
                        {/* Cell badge */}
                        <button
                          type="button"
                          disabled={isLocked}
                          onClick={() => handleCellClick(emp.id, dateStr)}
                          className={cn(
                            'w-full rounded px-0.5 py-1 transition-colors',
                            // holiday/weekly_off always win; override only adds the ring indicator
                            (dayType === 'holiday' || dayType === 'weekly_off')
                              ? cn(DAY_TYPE_STYLES[dayType], override && OVERRIDE_RING)
                              : override
                                ? cn(shiftColor(override.shift_id), OVERRIDE_RING)
                                : dayType === 'half_day'
                                  ? DAY_TYPE_STYLES.half_day
                                  : emp.shift_id ? shiftColor(emp.shift_id) : 'text-slate-300 dark:text-slate-600',
                            !isLocked && 'hover:opacity-80 cursor-pointer',
                            isLocked && 'cursor-default',
                          )}
                          title={
                            dayType === 'holiday'
                              ? `Holiday: ${holidayMap[dateStr]}${override ? ` (override: ${override.shift_name})` : ''}`
                              : dayType === 'weekly_off'
                                ? `Weekly off${override ? ` (override: ${override.shift_name})` : ''}`
                                : override
                                  ? `Override: ${override.shift_name}`
                                  : dayType === 'half_day'
                                    ? 'Half day'
                                    : emp.default_shift_name
                                      ? `Default: ${emp.default_shift_name}`
                                      : ''
                          }
                        >
                          {dayType === 'holiday'
                            ? 'HOL'
                            : dayType === 'weekly_off'
                              ? 'OFF'
                              : override
                                ? override.shift_name.slice(0, 4)
                                : dayType === 'half_day'
                                  ? `½ ${emp.default_shift_name?.slice(0, 3) ?? ''}`
                                  : emp.default_shift_name?.slice(0, 4) ?? '—'}
                        </button>

                        {/* Popover */}
                        {isOpen && (
                          <div data-popover="true">
                            <CellPopover
                              employee={emp}
                              date={dateStr}
                              override={override}
                              shifts={shifts}
                              onAssign={(shiftId) =>
                                bulkAssign.mutate({ employee_ids: [emp.id], shift_id: shiftId, dates: [dateStr] })
                              }
                              onRemove={() => removeEntry.mutate({ employee_id: emp.id, date: dateStr })}
                              onClose={() => setActiveCell(null)}
                            />
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
